import { publicApi } from '../utils/api';

const STORAGE_KEY = 'wowrolla_finder_session';
const ONE_DAY_MS = 24 * 60 * 60 * 1000;

const getFinderStorage = (persistent = false) => {
  if (typeof window === 'undefined') return null;
  try {
    return persistent ? window.localStorage : window.sessionStorage;
  } catch {
    return null;
  }
};

const STAGE_LABELS = ['Discovery', 'Context', 'Matching', 'Recommend', 'Implement'];

const STAGE_CHIPS = {
  0: [
    'Writing content & copy',
    'Finding & qualifying leads',
    'Automating workflows & integrations',
    'Managing email & follow-ups',
    'Video & social media creation',
    'Tracking, analytics & reporting',
    'Customer support & chat',
    'HR, hiring, or onboarding',
    "I don't know - help me explore",
  ],
  1: [
    'Just me (solo)',
    '2-5 people',
    '6-20 people',
    '21-100 people',
    '100+ enterprise',
    'Distributed contractors',
  ],
  2: [
    'Free tools only',
    'Under $50/month',
    '$50-$200/month',
    '$200-$500/month',
    '$500+/month - ROI is the metric',
  ],
  3: [
    'Founder / CEO',
    'Marketing lead',
    'Sales lead',
    'Operations / COO',
    'Developer / CTO',
    'HR / People ops',
    'Agency owner',
  ],
  postRecommend: ['Add all to stack', 'Compare these', 'Find more tools', 'Book a call', 'Start over'],
  postConsulting: ['Yes, book a call', "I'll set it up myself", 'Tell me more'],
};

const PAIN_MAP = {
  'writing content & copy': ['copywriting', 'content', 'seo', 'writing', 'blog'],
  'finding & qualifying leads': ['crm', 'sales', 'research', 'leads', 'prospecting'],
  'automating workflows & integrations': ['automation', 'workflow', 'integration', 'zapier', 'n8n', 'make'],
  'managing email & follow-ups': ['email', 'automation', 'outreach', 'newsletter'],
  'marketing through email': ['email', 'marketing', 'automation', 'newsletter', 'outreach'],
  'email marketing': ['email', 'marketing', 'automation', 'newsletter'],
  'video & social media creation': ['video', 'content', 'social', 'media'],
  'tracking, analytics & reporting': ['analytics', 'seo', 'reporting', 'data'],
  'customer support & chat': ['support', 'chat', 'helpdesk', 'customer'],
  'hr, hiring, or onboarding': ['hr', 'operations', 'hiring', 'recruitment'],
  "i don't know - help me explore": ['automation', 'copywriting', 'content'],
};

const TEAM_MAP = {
  'just me (solo)': { userType: 'solo', size: 1 },
  '2-5 people': { userType: 'smb', size: 3 },
  '6-20 people': { userType: 'smb', size: 10 },
  '21-100 people': { userType: 'smb', size: 50 },
  '100+ enterprise': { userType: 'enterprise', size: 120 },
  'distributed contractors': { userType: 'smb', size: 8 },
};

const BUDGET_MAP = {
  'free tools only': { tier: ['free'], label: 'free' },
  'under $50/month': { tier: ['free', '$'], label: 'under_50' },
  '$50-$200/month': { tier: ['$', '$$'], label: '50_200' },
  '$200-$500/month': { tier: ['$$', '$$$'], label: '200_500' },
  '$500+/month - roi is the metric': { tier: ['$$', '$$$'], label: '500_plus' },
};

function textOf(input) {
  return (input || '').trim();
}

function lower(input) {
  return textOf(input).toLowerCase();
}

function inferTeamFromText(input) {
  const normalized = lower(input);
  const numberMatch = normalized.match(/\b(\d{1,4})\b/);
  const explicit = numberMatch ? Number(numberMatch[1]) : null;

  if (normalized.includes('solo') || normalized.includes('just me') || explicit === 1) {
    return { userType: 'solo', size: 1, label: 'Just me (solo)' };
  }
  if (explicit && explicit <= 5) return { userType: 'smb', size: explicit, label: '2-5 people' };
  if (explicit && explicit <= 20) return { userType: 'smb', size: explicit, label: '6-20 people' };
  if (explicit && explicit <= 100) return { userType: 'smb', size: explicit, label: '21-100 people' };
  if (explicit && explicit > 100) return { userType: 'enterprise', size: explicit, label: '100+ enterprise' };
  if (normalized.includes('enterprise')) return { userType: 'enterprise', size: 120, label: '100+ enterprise' };
  if (normalized.includes('contractor') || normalized.includes('freelancer')) return { userType: 'smb', size: 8, label: 'Distributed contractors' };
  return null;
}

function inferBudgetFromText(input) {
  const normalized = lower(input);
  if (normalized.includes('free') || normalized.includes('no budget')) {
    return { tier: ['free'], label: 'free', display: 'Free tools only' };
  }

  const numbers = normalized.match(/\$?\s*(\d+(?:\.\d+)?)/g);
  const parsed = (numbers || []).map((n) => Number(String(n).replace(/[^0-9.]/g, ''))).filter((n) => !Number.isNaN(n));
  const maxValue = parsed.length ? Math.max(...parsed) : null;

  if (maxValue !== null && maxValue < 50) return { tier: ['free', '$'], label: 'under_50', display: 'Under $50/month' };
  if (maxValue !== null && maxValue <= 200) return { tier: ['$', '$$'], label: '50_200', display: '$50-$200/month' };
  if (maxValue !== null && maxValue <= 500) return { tier: ['$$', '$$$'], label: '200_500', display: '$200-$500/month' };
  if (maxValue !== null && maxValue > 500) return { tier: ['$$', '$$$'], label: '500_plus', display: '$500+/month - ROI is the metric' };

  if (normalized.includes('under 50')) return { tier: ['free', '$'], label: 'under_50', display: 'Under $50/month' };
  if (normalized.includes('50') && normalized.includes('200')) return { tier: ['$', '$$'], label: '50_200', display: '$50-$200/month' };
  if (normalized.includes('200') && normalized.includes('500')) return { tier: ['$$', '$$$'], label: '200_500', display: '$200-$500/month' };
  return null;
}

function inferRoleFromText(input) {
  const normalized = lower(input);
  if (normalized.includes('founder') || normalized.includes('ceo')) return 'Founder / CEO';
  if (normalized.includes('marketing')) return 'Marketing lead';
  if (normalized.includes('sales')) return 'Sales lead';
  if (normalized.includes('operation') || normalized.includes('coo')) return 'Operations / COO';
  if (normalized.includes('developer') || normalized.includes('cto') || normalized.includes('engineer')) return 'Developer / CTO';
  if (normalized.includes('hr') || normalized.includes('people')) return 'HR / People ops';
  if (normalized.includes('agency')) return 'Agency owner';
  return textOf(input);
}

function hasQuestionIntent(input) {
  const normalized = lower(input);
  if (!normalized) return false;
  if (normalized.includes('?')) return true;
  return /^(how|what|why|which|can|should|could|would|is|are|do|does|help|explain|compare|recommend)\b/.test(normalized);
}

function hasTeamAnswerSignal(input) {
  const normalized = lower(input);
  if (TEAM_MAP[normalized]) return true;
  return Boolean(inferTeamFromText(input));
}

function hasBudgetAnswerSignal(input) {
  const normalized = lower(input);
  if (BUDGET_MAP[normalized]) return true;
  return Boolean(inferBudgetFromText(input));
}

function hasRoleAnswerSignal(input) {
  const normalized = lower(input);
  if (!normalized) return false;
  return /(founder|ceo|marketing|sales|operations|coo|developer|cto|engineer|hr|people ops|agency)/.test(normalized);
}

function parseCostTier(tool) {
  const priceBlob = (tool.pricing_tiers || [])
    .map((tier) => `${tier.name || ''} ${tier.price || ''}`)
    .join(' ')
    .toLowerCase();

  if (!priceBlob) return '$$';
  if (priceBlob.includes('free') || priceBlob.includes('$0')) return 'free';

  const m = priceBlob.match(/\$\s*(\d+(?:\.\d+)?)/);
  if (!m) return '$$';
  const value = Number(m[1]);
  if (Number.isNaN(value)) return '$$';
  if (value < 50) return '$';
  if (value <= 200) return '$$';
  return '$$$';
}

function parseCookieDays(tool) {
  const days = tool.affiliate_link?.cookie_days;
  if (typeof days === 'number') return days;
  if (typeof days === 'string') {
    const n = Number(days.replace(/[^\d]/g, ''));
    return Number.isNaN(n) ? 30 : n;
  }
  return 30;
}

function recurringFlag(tool) {
  const recurring = tool.affiliate_link?.is_recurring;
  if (typeof recurring === 'boolean') return recurring;
  const comm = (tool.affiliate_link?.commission || '').toLowerCase();
  return comm.includes('recurring') || comm.includes('lifetime');
}

function categoryBlob(tool) {
  return `${tool.category_name || ''} ${tool.tagline || ''} ${tool.description || ''} ${(tool.features || []).join(' ')}`.toLowerCase();
}

export class FindAgent {
  constructor(
    tools,
    onMessage,
    onChips,
    onTools,
    onStageChange,
    onConsultingTrigger,
    onContext,
    options = {}
  ) {
    this.tools = tools || [];
    this.onMessage = onMessage;
    this.onChips = onChips;
    this.onTools = onTools;
    this.onStageChange = onStageChange;
    this.onConsultingTrigger = onConsultingTrigger;
    this.onContext = onContext;
    this.onReset = options.onReset || null;
    this.persistentStorage = Boolean(options.persistentStorage);
    this.liveStats = options.liveStats || {};
    this.stage = 0;
    this.context = {
      needle: options.preselectedNeedle || [],
      ...(options.seedContext || {}),
    };
    this.history = [];
    this.sessionId = options.sessionId;
  }

  static restoreFromStorage(options = {}) {
    const persistent = Boolean(options?.persistentStorage);
    try {
      const raw = getFinderStorage(persistent)?.getItem(STORAGE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed.updatedAt || Date.now() - parsed.updatedAt > ONE_DAY_MS) return null;
      return parsed;
    } catch {
      return null;
    }
  }

  static clearStorage(options = {}) {
    const persistent = Boolean(options?.persistentStorage);
    getFinderStorage(persistent)?.removeItem(STORAGE_KEY);
  }

  persist() {
    const payload = {
      updatedAt: Date.now(),
      stage: this.stage,
      context: this.context,
      history: this.history.slice(-20),
      sessionId: this.sessionId,
    };
    getFinderStorage(this.persistentStorage)?.setItem(STORAGE_KEY, JSON.stringify(payload));
  }

  emitMessage(role, content, meta = {}) {
    const msg = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
      role,
      content,
      createdAt: new Date().toISOString(),
      ...meta,
    };
    this.history.push({ role, content });
    this.onMessage(msg);
    this.persist();
    return msg;
  }

  setStage(nextStage) {
    this.stage = nextStage;
    this.onStageChange(nextStage, STAGE_LABELS[nextStage]);
    if (typeof this.onContext === 'function') {
      this.onContext({ ...this.context });
    }
    this.persist();
  }

  setChips(chips) {
    this.onChips((chips || []).map((label) => ({ label })));
  }

  async start() {
    const toolCount = Number(this.liveStats.toolCount || this.tools.length || 0);
    const categoryCount = Number(
      this.liveStats.categoryCount ||
      new Set(
        (this.tools || [])
          .map((tool) => tool.category_id || tool.category_name)
          .filter(Boolean)
      ).size ||
      0
    );

    const groundingLine = toolCount > 0
      ? `Welcome - I'm Wowrolla's AI advisor. Grounded in ${toolCount} AI tools across ${categoryCount} categories and practical implementation data.`
      : "Welcome - I'm Wowrolla's AI advisor. Grounded in live tool and implementation data.";

    this.emitMessage('assistant', groundingLine);
    this.emitMessage('assistant', "What's eating your time right now? Say it plainly and I'll map the stack. You can ask follow-up questions any time.");
    this.setStage(0);
    this.setChips(STAGE_CHIPS[0]);
  }

  applyPrefill(prefill = {}) {
    const painPoint = textOf(prefill.painPoint || '');
    const role = textOf(prefill.role || '');
    const budget = textOf(prefill.budget || '');

    if (painPoint) {
      this.context.painPoint = painPoint;
      this.context.focus = PAIN_MAP[lower(painPoint)] || ['automation'];
      this.setStage(1);
      this.setChips(STAGE_CHIPS[1]);
    }

    if (role) {
      this.context.role = role;
      // Requested behavior: role prefill can skip to stage 2 context refinement.
      if (this.stage < 2) {
        this.setStage(2);
        this.setChips(STAGE_CHIPS[2]);
      }
    }

    if (budget) {
      this.context.budget = budget;
      this.setStage(3);
      this.setChips(STAGE_CHIPS[3]);
    }
  }

  async hydrate(saved) {
    this.stage = saved.stage ?? 0;
    this.context = saved.context || { needle: [] };
    this.history = saved.history || [];
    this.sessionId = saved.sessionId || this.sessionId;
    this.onStageChange(this.stage, STAGE_LABELS[this.stage]);
    if (typeof this.onContext === 'function') this.onContext({ ...this.context });
    const stageChips = this.stage <= 3 ? STAGE_CHIPS[this.stage] : STAGE_CHIPS.postRecommend;
    this.setChips(stageChips);
  }

  async handleUserInput(text) {
    const input = textOf(text);
    if (!input) return;
    const normalizedInput = lower(input);

    this.emitMessage('user', input);

    if (normalizedInput === 'start over') {
      this.stage = 0;
      this.context = { needle: [] };
      this.history = [];
      getFinderStorage()?.removeItem(STORAGE_KEY);
      if (typeof this.onReset === 'function') this.onReset();
      this.onTools([]);
      this.emitMessage('assistant', "Clean slate. What problem should I solve first?");
      this.setStage(0);
      this.setChips(STAGE_CHIPS[0]);
      return;
    }

    if (this.stage === 0) {
      return this.handleStage0(input);
    }
    if (this.stage === 1) {
      return this.handleStage1(input);
    }
    if (this.stage === 2) {
      return this.handleStage2(input);
    }
    if (this.stage === 3) {
      return this.handleStage3(input);
    }

    if (lower(input) === 'add all to stack') {
      const picks = (this.context.recommendedTools || []).map((t) => t.id);
      this.context.needle = Array.from(new Set([...(this.context.needle || []), ...picks]));
      this.emitMessage('assistant', `Added ${picks.length} tools to your stack draft. Want to compare them side-by-side or go straight to implementation?`);
      const complexity = this.assessComplexity();
      if (complexity >= 4) {
        this.onConsultingTrigger(complexity, this.context);
        this.setChips(STAGE_CHIPS.postConsulting);
      } else {
        this.setChips(STAGE_CHIPS.postRecommend);
      }
      return;
    }

    if (lower(input) === 'find more tools') {
      const extra = this.matchTools(new Set((this.context.recommendedTools || []).map((t) => t.id)), 3);
      this.context.recommendedTools = extra;
      this.onTools(extra);
      this.emitMessage('assistant', 'Here are more options that still fit your profile and budget.');
      this.setChips(STAGE_CHIPS.postRecommend);
      return;
    }

    return this.handleFreeText(input);
  }

  async handleStage0(input) {
    const labels = input
      .split(',')
      .map((item) => textOf(item))
      .filter(Boolean)
      .slice(0, 3);

    // Stopwords to skip when extracting free-text keywords
    const STOPWORDS = new Set(['through', 'with', 'and', 'for', 'the', 'that', 'this', 'from', 'into', 'using', 'want', 'need', 'help', 'more', 'better', 'good', 'great']);

    const focusGroups = labels.map((label) => {
      const mapped = PAIN_MAP[lower(label)];
      if (mapped) return { label, keywords: mapped };
      // Free-text: extract meaningful individual words from the phrase
      const extracted = lower(label)
        .split(/\s+/)
        .filter((w) => w.length > 3 && !STOPWORDS.has(w));
      // Also try partial PAIN_MAP matches by checking if any key contains words from input
      const partialMatch = Object.entries(PAIN_MAP).find(([key]) =>
        extracted.some((w) => key.includes(w))
      );
      const keywords = partialMatch
        ? [...partialMatch[1], ...extracted.slice(0, 2)]
        : extracted.length ? extracted : [lower(label)];
      return { label, keywords: Array.from(new Set(keywords)) };
    });

    const unionKeywords = Array.from(
      new Set(focusGroups.flatMap((group) => group.keywords || []))
    ).filter(Boolean);

    this.context.painPoint = labels.join(', ') || input;
    this.context.focusGroups = focusGroups;
    this.context.focus = unionKeywords.length ? unionKeywords : ['automation'];
    this.emitMessage('assistant', 'Got it. Team size next - this changes what will actually work in practice.');
    this.setStage(1);
    this.setChips(STAGE_CHIPS[1]);
  }

  async handleStage1(input) {
    if (hasQuestionIntent(input) && !hasTeamAnswerSignal(input)) {
      await this.handleFreeText(input);
      this.emitMessage('assistant', 'Quick profile check so I can personalize better: what is your team size?');
      this.setStage(1);
      this.setChips(STAGE_CHIPS[1]);
      return;
    }

    const key = lower(input);
    const inferred = inferTeamFromText(input);
    const mapped = inferred || TEAM_MAP[key] || { userType: 'smb', size: 5, label: input };
    this.context.teamSize = inferred?.label || input;
    this.context.userType = mapped.userType;
    this.context.teamSizeNumeric = mapped.size;
    this.emitMessage('assistant', 'Helpful. What monthly budget range should I respect?');
    this.setStage(2);
    this.setChips(STAGE_CHIPS[2]);
  }

  async handleStage2(input) {
    if (hasQuestionIntent(input) && !hasBudgetAnswerSignal(input)) {
      await this.handleFreeText(input);
      this.emitMessage('assistant', 'I can keep it practical. What monthly budget range should I use for recommendations?');
      this.setStage(2);
      this.setChips(STAGE_CHIPS[2]);
      return;
    }

    const key = lower(input);
    const inferred = inferBudgetFromText(input);
    const mapped = inferred || BUDGET_MAP[key] || { tier: ['$', '$$'], label: '50_200', display: input };
    this.context.budget = mapped.display || input;
    this.context.costTiers = mapped.tier;
    this.context.budgetLabel = mapped.label;
    this.emitMessage('assistant', 'Last context check: what is your role?');
    this.setStage(3);
    this.setChips(STAGE_CHIPS[3]);
  }

  async handleStage3(input) {
    if (hasQuestionIntent(input) && !hasRoleAnswerSignal(input)) {
      await this.handleFreeText(input);
      this.emitMessage('assistant', 'Before I finalize the stack, what role are you in this decision?');
      this.setStage(3);
      this.setChips(STAGE_CHIPS[3]);
      return;
    }

    this.context.role = inferRoleFromText(input);
    const tools = this.matchTools(new Set(), 6);
    this.context.recommendedTools = tools;
    this.onTools(tools);
    this.emitMessage(
      'assistant',
      "Here are your top fits grouped across your selected workflows (2 picks per workflow focus) and ranked by profile alignment - never by commission.",
      { tools }
    );
    this.setStage(4);
    this.setChips(STAGE_CHIPS.postRecommend);

    const complexity = this.assessComplexity();
    if (complexity >= 4) {
      this.onConsultingTrigger(complexity, this.context);
      this.setChips(STAGE_CHIPS.postConsulting);
    }
  }

  matchTools(excludeIds = new Set(), limit = 6) {
    const focus = (this.context.focus || []).map((f) => f.toLowerCase());
    const focusGroups = (this.context.focusGroups || []).slice(0, 3);
    const allowedCosts = this.context.costTiers || ['$', '$$'];
    const userType = this.context.userType || 'smb';

    const scored = this.tools
      .filter((tool) => !excludeIds.has(tool.id))
      .map((tool) => {
        const blob = categoryBlob(tool);
        const catId = (tool.category_id || '').toLowerCase();
        const catName = (tool.category_name || '').toLowerCase();
        const toolName = (tool.name || '').toLowerCase();

        // Category/focus match - check blob text AND category id/name directly
        const catMatch = focus.some(
          (f) => blob.includes(f) || catId.includes(f) || catName.includes(f) || toolName.includes(f)
        );
        // Stronger match: multiple focus keywords match
        const catMatchCount = focus.filter(
          (f) => blob.includes(f) || catId.includes(f) || catName.includes(f)
        ).length;
        const catScore = catMatch ? Math.min(6, 3 + catMatchCount) : 0;

        const costTier = parseCostTier(tool);
        // Free tools always match any budget
        const costMatch = allowedCosts.includes(costTier) || costTier === 'free';
        const costScore = costMatch ? 3 : 0;

        const isEnterprise = blob.includes('enterprise') || blob.includes('sso') || blob.includes('soc2');
        const userMatch =
          userType === 'enterprise'
            ? isEnterprise || !blob.includes('solo')
            : userType === 'solo'
              ? !isEnterprise
              : true;
        const userScore = userMatch ? 2 : 0;

        // fitScore is based purely on relevance (not affiliate metrics)
        // Max possible: catScore(6) + costScore(3) + userScore(2) = 11
        const fitPoints = catScore + costScore + userScore;
        const fitScore = Math.min(100, Math.round((fitPoints / 11) * 100));

        // Internal sorting score can include affiliate signals (minor weight)
        const recurring = recurringFlag(tool);
        const cookieDays = parseCookieDays(tool);
        const finderScore = fitPoints + (recurring ? 0.3 : 0) + cookieDays / 300;

        return {
          ...tool,
          finderScore: Number(finderScore.toFixed(2)),
          fitScore,
          costTier,
          cookieDays,
          recurring,
        };
      })
      .sort((a, b) => b.finderScore - a.finderScore);

    // If the user selected multiple stage-0 workflows, enforce balanced picks:
    // up to 2 tools per selected workflow focus, up to 3 focuses total.
    if (focusGroups.length > 0) {
      const selected = [];
      const usedIds = new Set();

      focusGroups.forEach((group) => {
        const keywords = (group.keywords || []).map((k) => lower(k)).filter(Boolean);
        const perGroup = scored
          .filter((tool) => {
            if (usedIds.has(tool.id)) return false;
            const blob = categoryBlob(tool);
            return keywords.some((keyword) => blob.includes(keyword));
          })
          .slice(0, 2);

        perGroup.forEach((tool) => {
          usedIds.add(tool.id);
          selected.push(tool);
        });
      });

      if (selected.length < limit) {
        const extras = scored.filter((tool) => !usedIds.has(tool.id)).slice(0, limit - selected.length);
        return [...selected, ...extras];
      }

      return selected.slice(0, limit);
    }

    return scored.slice(0, limit);
  }

  assessComplexity() {
    const recommended = this.context.recommendedTools || [];
    const needleIds = new Set(this.context.needle || []);
    const selected = recommended.filter((tool) => needleIds.has(tool.id));

    let complexity = (this.context.needle || []).length * 2;
    const categories = new Set(selected.map((tool) => tool.category_name || tool.category_id || 'other'));
    if (categories.size >= 2) complexity += 2;

    const highComplexity = selected.some((tool) => (tool.implementation_complexity || '').toLowerCase() === 'high');
    if (highComplexity) complexity += 3;

    return complexity;
  }

  async handleFreeText(text) {
    try {
      const res = await publicApi.findChat({
        context: this.context,
        history: this.history.slice(-10),
        message: text,
        sessionId: this.sessionId,
      });
      const payload = res.data || {};
      if (payload.sessionId && !this.sessionId) this.sessionId = payload.sessionId;
      // Check if session limit has been reached
      if (payload.sessionLimitReached) {
        this.emitMessage('assistant', payload.response || 'Session question limit reached.');
        this.setChips(payload.suggestedChips || ['Book a call', 'Start over']);
        this.context.sessionLimitReached = true;
        if (typeof this.onContext === 'function') this.onContext({ ...this.context });
        return;
      }

      // Store question limit info for UI display
      this.context.questionsRemaining = payload.questionsRemaining || 0;

      const responseText = payload.response || 'I have your context. Want me to rank another set of tools?';
      this.emitMessage('assistant', responseText);
      this.onChips((payload.suggestedChips || []).map((label) => ({ label })));

      if ((payload.detectedTools || []).length) {
        const names = new Set(payload.detectedTools.map((name) => name.toLowerCase()));
        const highlighted = this.tools.filter((tool) => names.has((tool.name || '').toLowerCase())).slice(0, 3);
        if (highlighted.length) {
          this.context.recommendedTools = highlighted;
          this.onTools(highlighted);
        }
      }

      if ((payload.consultingScore || 0) >= 4) {
        this.onConsultingTrigger(payload.consultingScore, this.context);
        this.setChips(STAGE_CHIPS.postConsulting);
      }
    } catch {
      // Provide varied, contextual error messages so the same text never repeats
      const errorMessages = [
        "The server took too long - try rephrasing your question or ask about a specific tool.",
        "Network hiccup. Your question was recorded. Type it again and I'll answer straight away.",
        "Something went wrong on my end. Ask about a specific tool name or workflow and I'll look it up.",
        "Temporary issue. Try a shorter question - e.g. 'best automation tool under $50'.",
      ];
      const idx = Math.floor(Math.random() * errorMessages.length);
      this.emitMessage('assistant', errorMessages[idx]);
      this.setChips(STAGE_CHIPS.postRecommend);
    }
  }

  async persistServerSession(outcome = 'completed') {
    try {
      await publicApi.saveFindSession({
        sessionId: this.sessionId,
        context: this.context,
        needle: this.context.needle || [],
        outcome,
      });
    } catch {
      // Best effort persistence only.
    }
  }
}

export { STAGE_LABELS, STAGE_CHIPS };

